
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import CategoryFilters from "./CategoryFilters";
import CategoryTabs from "./CategoryTabs";
import CategoryFormDialog from "./CategoryFormDialog";
import CategoryMergeDialog from "./CategoryMergeDialog";
import CategoryActionButtons from "./CategoryActionButtons";
import { CategoryFormData } from "./types";
import { useCategoryManagement } from "@/hooks/useCategoryManagement";

const CategoryManager = () => {
  const [formData, setFormData] = useState<CategoryFormData>({
    name: "",
    slug: "",
    description: "",
    icon: "",
    parent_id: null,
    keywords: []
  });
  const [keywordsInput, setKeywordsInput] = useState("");
  const [mergeSource, setMergeSource] = useState<string>("");
  const [mergeTarget, setMergeTarget] = useState<string>("");
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [showEditCategoryForm, setShowEditCategoryForm] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  
  const { isAdmin } = useAuth();
  const { 
    categories, 
    filteredCategories, 
    isLoading, 
    searchQuery, 
    sortBy,
    setSortBy,
    selectedCategory,
    setSelectedCategory,
    handleSearch, 
    handleSaveCategory, 
    handleEditCategory, 
    handleArchiveCategory, 
    handleMergeCategories 
  } = useCategoryManagement();
  
  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      icon: "",
      parent_id: null,
      keywords: []
    });
    setKeywordsInput("");
  };
  
  const handleAddKeyword = () => {
    if (!keywordsInput.trim()) return;
    
    const newKeywords = keywordsInput
      .split(",")
      .map(k => k.trim())
      .filter(Boolean);
    
    setFormData({
      ...formData,
      keywords: [...(formData.keywords || []), ...newKeywords]
    });
    
    setKeywordsInput("");
  };
  
  const handleRemoveKeyword = (keyword: string) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter(k => k !== keyword)
    });
  };
  
  const handleEditButtonClick = (category: any) => {
    const formValues = handleEditCategory(category);
    setFormData(formValues);
    setShowEditCategoryForm(true);
  };
  
  const onSaveCategory = async () => {
    const success = await handleSaveCategory(formData);
    if (success) {
      // Close dialogs
      setShowNewCategoryForm(false);
      setShowEditCategoryForm(false);
      
      // Reset form
      resetForm();
      setSelectedCategory(null);
    }
  };
  
  const onMergeCategories = async () => {
    const success = await handleMergeCategories(mergeSource, mergeTarget);
    if (success) {
      // Close dialog and reset state
      setShowMergeDialog(false);
      setMergeSource("");
      setMergeTarget("");
    }
  };
  
  const openNewCategoryForm = () => {
    resetForm();
    setShowNewCategoryForm(true);
  };
  
  const openMergeDialog = () => {
    setShowMergeDialog(true);
  };
  
  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h3 className="text-xl font-medium mb-2">Admin access required</h3>
        <p className="text-muted-foreground">
          You need admin privileges to manage categories.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Category Management</h2>
        <CategoryActionButtons
          openNewCategoryForm={openNewCategoryForm}
          openMergeDialog={openMergeDialog}
          showNewCategoryForm={showNewCategoryForm}
          setShowNewCategoryForm={setShowNewCategoryForm}
        />
      </div>
      
      <CategoryFilters
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      
      <CategoryTabs
        categories={categories}
        filteredCategories={filteredCategories}
        isLoading={isLoading}
        searchQuery={searchQuery}
        handleEditCategory={handleEditButtonClick}
        handleArchiveCategory={handleArchiveCategory}
      />
      
      <CategoryFormDialog
        open={showNewCategoryForm}
        onOpenChange={setShowNewCategoryForm}
        title="Create New Category"
        description="Add a new category for organizing prompts."
        formData={formData}
        setFormData={setFormData}
        keywordsInput={keywordsInput}
        setKeywordsInput={setKeywordsInput}
        handleAddKeyword={handleAddKeyword}
        handleRemoveKeyword={handleRemoveKeyword}
        handleSaveCategory={onSaveCategory}
        resetForm={resetForm}
        categories={categories}
        isEditMode={false}
      />
      
      <CategoryFormDialog
        open={showEditCategoryForm}
        onOpenChange={setShowEditCategoryForm}
        title="Edit Category"
        description="Update the details for this category."
        formData={formData}
        setFormData={setFormData}
        keywordsInput={keywordsInput}
        setKeywordsInput={setKeywordsInput}
        handleAddKeyword={handleAddKeyword}
        handleRemoveKeyword={handleRemoveKeyword}
        handleSaveCategory={onSaveCategory}
        resetForm={resetForm}
        categories={categories}
        isEditMode={true}
      />
      
      <CategoryMergeDialog
        open={showMergeDialog}
        onOpenChange={setShowMergeDialog}
        categories={categories}
        mergeSource={mergeSource}
        setMergeSource={setMergeSource}
        mergeTarget={mergeTarget}
        setMergeTarget={setMergeTarget}
        handleMergeCategories={onMergeCategories}
      />
    </div>
  );
};

export default CategoryManager;
